import { z } from 'zod'

import { zSelect } from 'Lib/zod'

import { FORM_MESSAGES_MAP } from 'Constants/formMessages'
import { RETURN_LABEL_SENDER_NAME_MAX_LENGTH } from 'Constants/returnLabel'

import {
  PORTAL_RETURN_LABEL_ADDRESS_FIELD_MAX_LENGTH,
  PORTAL_RETURN_LABEL_MAX_DEVICES,
} from './PortalReturnLabelModal.constants'
import {
  PortalReturnLabelAddressSourceEnum,
  type PortalReturnLabelSelectableReturnCase,
} from './PortalReturnLabelModal.types'

// SoT for "a valid sender address". Used both for the parent form schema and
// for the standalone safeParse check that gates the incomplete-office warning,
// so the warning fires exactly when submit validation would block.
export const portalReturnLabelAddressSchema = z.object({
  city: z
    .string()
    .trim()
    .min(1, FORM_MESSAGES_MAP.REQUIRED)
    .max(
      PORTAL_RETURN_LABEL_ADDRESS_FIELD_MAX_LENGTH,
      FORM_MESSAGES_MAP.MAX_LENGTH,
    ),
  country: zSelect.refine(arg => !!arg?.value, FORM_MESSAGES_MAP.REQUIRED),
  email: z.string().trim().email(FORM_MESSAGES_MAP.EMAIL).or(z.literal('')).optional(),
  // Becomes the name on the sender address DHL prints, so it takes the sender
  // name limit - stricter than the generic address-field cap the other lines use.
  fullName: z
    .string()
    .trim()
    .min(1, FORM_MESSAGES_MAP.REQUIRED)
    .max(RETURN_LABEL_SENDER_NAME_MAX_LENGTH, FORM_MESSAGES_MAP.MAX_LENGTH),
  phoneNumber: z.string().trim().optional(),
  street: z
    .string()
    .trim()
    .min(1, FORM_MESSAGES_MAP.REQUIRED)
    .max(
      PORTAL_RETURN_LABEL_ADDRESS_FIELD_MAX_LENGTH,
      FORM_MESSAGES_MAP.MAX_LENGTH,
    ),
  streetNumber: z
    .string()
    .trim()
    .min(1, FORM_MESSAGES_MAP.REQUIRED)
    .max(
      PORTAL_RETURN_LABEL_ADDRESS_FIELD_MAX_LENGTH,
      FORM_MESSAGES_MAP.MAX_LENGTH,
    ),
  zipcode: z
    .string()
    .trim()
    .min(1, FORM_MESSAGES_MAP.REQUIRED)
    .max(
      PORTAL_RETURN_LABEL_ADDRESS_FIELD_MAX_LENGTH,
      FORM_MESSAGES_MAP.MAX_LENGTH,
    ),
})

// The primary return case counts as device 1, so addedReturnCases caps at
// MAX_DEVICES - 1. The UI gate hides the Add trigger before this is hit;
// the schema is the SoT for any path that bypasses the gate.
export const portalReturnLabelModalFormSchema =
  portalReturnLabelAddressSchema.extend({
    addedReturnCases: z
      .array(z.custom<PortalReturnLabelSelectableReturnCase>())
      .max(PORTAL_RETURN_LABEL_MAX_DEVICES - 1, FORM_MESSAGES_MAP.MAX_LENGTH)
      .default([]),
    addressSource: z.nativeEnum(PortalReturnLabelAddressSourceEnum, {
      errorMap: () => ({ message: FORM_MESSAGES_MAP.REQUIRED }),
    }),
  })
